import { forEach } from '@grubhubprod/umami-library/dist/nodash/forEach';
import { includes } from '@grubhubprod/umami-library/dist/nodash/includes';
import { some } from '@grubhubprod/umami-library/dist/nodash/some';
import { isEmpty } from '@grubhubprod/umami-library/dist/nodash/isEmpty';
import { cloneDeep } from '../../dependencies/nodash/cloneDeep';
import { toQueryString } from '../../utilities/to-query-string';
import { AvailableOrderTypes } from '../../enums/available-order-types-enum';
import { Address } from '../address.model';
import {
  FacetSettings,
  Sort,
  DEFAULT_PAGE_SIZE,
  OrderMethod,
  FacetHashMap,
  DEFAULT_DELIVERY_TIME,
  SearchQueryBreadCrumb,
  FacetTuple,
} from './search-query-model-types';
import { SearchQuery } from './search-query.model';

const presetFacets = {
  standard: [
    // format is: category_id (NOT facet_id), facet value, link_fragment
    ['most_popular', 'Open Now', 'facet=open_now%3Atrue'],
  ],
  catering: [],
};

function initializeFacetCategory(
  searchQuery: SearchQuery,
  type: string,
): string {
  type = type.toLowerCase();
  searchQuery.extraParameters.facet = searchQuery.extraParameters.facet || {};
  searchQuery.extraParameters.facet[type] =
    searchQuery.extraParameters.facet[type] || [];

  return type;
}

function initializeFacetCountCategory(
  searchQuery: SearchQuery,
  type: string,
): string {
  type = type.toLowerCase();
  searchQuery.extraParameters.facetCount =
    searchQuery.extraParameters.facetCount || {};
  searchQuery.extraParameters.facetCount[type] =
    searchQuery.extraParameters.facetCount[type] || {};

  return type;
}

// TODO this is only temporary so that we do not hit the API with these facets thus breaking the API call
function isDisabledCategory(facetCategoryName: string): boolean {
  return includes(['distance'], facetCategoryName);
}

function getFacetTypeGroup(facetType: string): string {
  if (
    includes(
      [
        'coupons',
        'order_tracking_enabled',
        'open_now',
        'new_restaurant',
        'free_delivery',
        'not_delivery_service',
      ],
      facetType,
    )
  ) {
    return 'most_popular';
  }

  return facetType;
}

function getFacetValue(facetValue: string): string {
  const map = {
    coupons: 'Coupons Available',
    free_delivery: 'Free Delivery',
    new_restaurant: 'New',
    not_delivery_service: 'Hide Delivery Services',
    open_now: 'Open Now',
    order_tracking_enabled: 'Order Tracking',
  };

  return map[facetValue];
}

export function searchQueryUrl(
  searchQuery: SearchQuery,
  withQueryParameters?: boolean,
): string {
  if (!withQueryParameters) {
    return '/search';
  }

  const queryStringData = searchQueryString(searchQuery);
  let url = '/search';

  if (queryStringData) {
    url += '?' + queryStringData;
  }

  return url;
}

/**
 * Transforms the current search query to a complex query object for translation to a query string
 * @param wktFlag Boolean flag to output the address as location: `Point(lat long)` vs { latitude, longitude}
 */
export function makeQuery(searchQuery: SearchQuery, wktFlag?: boolean): any {
  const query: any = {};
  query.orderMethod = searchQuery.orderMethod;
  query.locationMode =
    searchQuery.locationMode || searchQuery.orderMethod.toUpperCase();
  query.facetSet = searchQuery.facetSet;
  query.pageSize =
    !!searchQuery.pageSize || searchQuery.pageSize === 0
      ? searchQuery.pageSize
      : DEFAULT_PAGE_SIZE;
  query.hideHateos = true; // this is the default for now
  query.searchMetrics =
    typeof searchQuery.searchMetrics === 'boolean'
      ? searchQuery.searchMetrics
      : true;

  if (searchQuery.queryText) {
    query.queryText = searchQuery.queryText;
  }

  if (searchQuery.location) {
    query.location = searchQuery.location;
  } else if (searchQuery.address) {
    if (!wktFlag) {
      query.latitude = searchQuery.address.latitude;
      query.longitude = searchQuery.address.longitude;
    } else {
      query.location =
        'POINT(' +
        searchQuery.address.longitude +
        ' ' +
        searchQuery.address.latitude +
        ')';
    }
  }

  if (searchQuery.sort) {
    const linkFragment = searchQuery.sort.linkFragment || '';
    const linkFragmentTokenizer = linkFragment.split('=');
    const sortKey = decodeURIComponent(linkFragmentTokenizer[0]);
    const sortValue = decodeURIComponent(linkFragmentTokenizer[1]);
    query[sortKey] = sortValue;
  }

  if (searchQuery.extraParameters.facet) {
    forEach(searchQuery.extraParameters.facet, (values, facetCategoryName) => {
      if (facetCategoryName) {
        forEach(values, (facet: any) => {
          const linkFragment = facet.linkFragment || '';
          const linkFragmentTokenizer = linkFragment.split('=');

          if (linkFragmentTokenizer.length === 2) {
            const facetKey = decodeURIComponent(linkFragmentTokenizer[0]);
            const facetValue = decodeURIComponent(linkFragmentTokenizer[1]);

            if (!query[facetKey]) {
              query[facetKey] = [];
            }

            if (!isDisabledCategory(facetCategoryName as any)) {
              query[facetKey].push(facetValue);
            }
          }
        });
      }
    });
  }

  if (searchQuery.extraParameters.facetCount) {
    forEach(searchQuery.extraParameters.facetCount, (count, facetCategory) => {
      if (!query.facetCount) {
        query.facetCount = [];
      }
      query.facetCount.push(`${facetCategory}:${count}`);
    });
  }

  if (
    searchQuery.orderMethod === OrderMethod.PICKUP &&
    searchQuery.extraParameters.distance
  ) {
    query.radius = searchQuery.extraParameters.distance;
  } else if (
    searchQuery.orderMethod === OrderMethod.PICKUP &&
    !query.radius &&
    searchQuery.address &&
    searchQuery.address.pickupRadius
  ) {
    query.radius = searchQuery.address.pickupRadius;
  }

  // Check for sort test variable set by optimizely

  if (searchQuery.variationId) {
    query.variationId = searchQuery.variationId;
  }

  if (searchQuery.fieldSet) {
    query.fieldSet = searchQuery.fieldSet;
  }

  if (searchQuery.sortSetId) {
    query.sortSetId = searchQuery.sortSetId;
  }

  if (searchQuery.sponsoredSize) {
    query.sponsoredSize = searchQuery.sponsoredSize;
  }

  query.countOmittingTimes =
    typeof searchQuery.countOmittingTimes === 'boolean'
      ? searchQuery.countOmittingTimes
      : true;

  if (searchQuery.whenFor) {
    query.whenFor = searchQuery.whenFor;
    query.isFutureOrder = searchQuery.whenFor ? 'true' : 'false';
  }

  // if whenFrom is active,
  // we must assume futureOrder and whenTo are active also

  if (searchQuery.whenFrom && searchQuery.whenTo) {
    query.whenFrom = searchQuery.whenFrom;
    query.whenTo = searchQuery.whenTo;
    query.isFutureOrder = !!searchQuery.whenFor;
  }

  if (searchQuery.contentTag) {
    query.contentTag = searchQuery.contentTag;
  }

  // This may need to change later,

  if (searchQuery.extraParameters.pageNum) {
    query.pageNum = searchQuery.extraParameters.pageNum;
  }

  // We only want to apply the searchId on pages gte 1

  if (searchQuery.searchId && searchQuery.extraParameters.pageNum >= 1) {
    query.searchId = searchQuery.searchId;
  }

  return query;
}

/**
 * Transforms the SearchQuery into a collection of querystring parameters
 * @param wktFlag Boolean flag to output the address as location: `Point(lat, long)` vs { latitude, longitude }
 */
export function searchQueryString(
  searchQuery: SearchQuery,
  wktFlag?: boolean,
): string {
  const queryData = makeQuery(searchQuery, wktFlag);
  return toQueryString(queryData);
}
/**
 * Takes a facet type and link fragment and generates
 * a hashed string representation.  For building a
 * facetHashMap.
 *
 * @param  { type: string, linkFragment: string }
 *
 * @returns string
 */

export function hashFacetObject({ type, linkFragment }) {
  const normalizedFragment = linkFragment.replace(/%20/g, '+');
  return type + ':' + normalizedFragment;
}

export function getFacetHashMap(searchQuery: SearchQuery): FacetHashMap {
  const facetHashMap = {};
  searchQuery.extraParameters.facet = searchQuery.extraParameters.facet || {};
  forEach(searchQuery.extraParameters.facet, (values, type) => {
    forEach(values, ({ linkFragment }) => {
      if (linkFragment) {
        const facetHash = hashFacetObject({ type, linkFragment });
        facetHashMap[facetHash] = true;
      }
    });
  });

  return facetHashMap;
}

export function presetFacetHashMap(searchQuery: SearchQuery): FacetHashMap {
  // Make memoized, presets hash map if unavailable.
  // This happens because not all uses of SearchQuery
  // call the method that makes preset facets
  if (!searchQuery.presetFacetHashMapMemo) {
    return setPresetFacetHashMap(searchQuery).presetFacetHashMapMemo;
  }
  return searchQuery.presetFacetHashMapMemo;
}

export function selectedFacetDescription(
  searchQuery: SearchQuery,
  type: string,
): string {
  let description = '';
  type = initializeFacetCategory(searchQuery, type);
  const facetList = getFacetList(searchQuery, type);

  if (facetList.length > 0) {
    if (facetList.length <= 4) {
      description = facetList.join(', ');
    } else {
      description = facetList.length + ' selected';
    }
  }

  return description;
}

export function addFacetCount(
  searchQuery: SearchQuery,
  type: string,
  count: number,
): SearchQuery {
  type = initializeFacetCountCategory(searchQuery, type);
  searchQuery.extraParameters.facetCount[type] = count;
  return searchQuery;
}

export function addSearchQueryFacet(
  searchQuery: SearchQuery,
  type: string,
  value: number | string,
  linkFragment: string,
): SearchQuery {
  type = initializeFacetCategory(searchQuery, type);
  // Important note:  the API is not URL encoding the + signs so we need to do it ourselves.

  if (linkFragment) {
    linkFragment = linkFragment.replace(/\+/g, '%20');
  }

  // Value is used for display in selectedFacetDescription
  const facet = {
    linkFragment,
    value,
  };

  // Removes any duplicates we find
  searchQuery = removeSearchQueryFacet(searchQuery, type, linkFragment);
  searchQuery.extraParameters.facet[type].push(facet);
  searchQuery = setPageSearchQuery(searchQuery);

  return searchQuery;
}

export function removeSearchQueryFacet(
  searchQuery: SearchQuery,
  type: string,
  linkFragment: string,
): SearchQuery {
  type = initializeFacetCategory(searchQuery, type);
  // Important note:  the API is not URL encoding the + signs so we need to do it ourselves.

  if (linkFragment) {
    linkFragment = linkFragment.replace(/\+/g, '%20');
  }

  if (Array.isArray(searchQuery.extraParameters.facet[type])) {
    searchQuery.extraParameters.facet[type] = searchQuery.extraParameters.facet[
      type
    ].filter((facet) => facet.linkFragment !== linkFragment);
  }

  searchQuery = setPageSearchQuery(searchQuery);

  return searchQuery;
}

export function removeFacetCount(searchQuery: SearchQuery, type: string) {
  if (searchQuery.extraParameters && searchQuery.extraParameters.facetCount) {
    delete searchQuery.extraParameters.facetCount[type];
  }

  return searchQuery;
}

export function clearSearchQueryFacet(
  searchQuery: SearchQuery,
  type?: string,
): SearchQuery {
  if (type) {
    initializeFacetCategory(searchQuery, type);
    searchQuery.extraParameters.facet[type] = [];
  } else {
    searchQuery.extraParameters.facet = {};
  }

  searchQuery = setPageSearchQuery(searchQuery);
  return searchQuery;
}

export function clearFacets(searchQuery: SearchQuery): SearchQuery {
  searchQuery.extraParameters.facet = {};
  searchQuery = setWhenFor(searchQuery, 'ASAP');
  searchQuery = setPresetValues(searchQuery);
  searchQuery = setPageSearchQuery(searchQuery);

  if (
    searchQuery.orderMethod === OrderMethod.PICKUP &&
    searchQuery.address &&
    searchQuery.address.pickupRadius
  ) {
    searchQuery.extraParameters.distance = searchQuery.address.pickupRadius;
  }

  return searchQuery;
}

export function clearNonCuisineFacets(searchQuery: SearchQuery): SearchQuery {
  if (
    !searchQuery.extraParameters.facet ||
    (!searchQuery.extraParameters.facet.cuisine &&
      !searchQuery.extraParameters.facet.browse_hierarchy)
  ) {
    return clearSearchQueryFacet(searchQuery);
  }

  const cuisine = searchQuery.extraParameters.facet.cuisine;
  const browseCuisine = searchQuery.extraParameters.facet.browse_hierarchy;
  searchQuery.extraParameters.facet = {};
  searchQuery.extraParameters.facet.cuisine = cuisine;
  searchQuery.extraParameters.facet.browse_hierarchy = browseCuisine;
  searchQuery = setWhenFor(searchQuery, 'ASAP');
  searchQuery = setPageSearchQuery(searchQuery);

  return searchQuery;
}

export function setSearchQueryText(
  searchQuery: SearchQuery,
  queryText: string,
): SearchQuery {
  searchQuery.queryText = queryText ? queryText.trim() : null;
  return searchQuery;
}

export function setDistance(
  searchQuery: SearchQuery,
  value?: any,
): SearchQuery {
  searchQuery.extraParameters.distance = value ? parseFloat(value) : null;

  return searchQuery;
}

export function getDistance(searchQuery: SearchQuery): number {
  return searchQuery.extraParameters.distance;
}

export function getDeliveryTime(searchQuery: SearchQuery): number {
  let value = DEFAULT_DELIVERY_TIME; // 75 minutes is the max value

  if (some(getFacetList(searchQuery, 'delivery_time'))) {
    value = getFacetList(searchQuery, 'delivery_time')[0];
  }

  return value;
}

export function setDeliveryTime(
  searchQuery: SearchQuery,
  value: number,
): SearchQuery {
  searchQuery = clearSearchQueryFacet(searchQuery, 'delivery_time');
  // when sending the max value
  // just clear the facet altogether

  if (value === DEFAULT_DELIVERY_TIME) {
    return searchQuery;
  }

  if (value) {
    searchQuery = addSearchQueryFacet(
      searchQuery,
      'delivery_time',
      value,
      'facet=delivery_time:' + value,
    );
  }

  return searchQuery;
}

export function setBayesianRating(
  searchQuery: SearchQuery,
  value: number,
): SearchQuery {
  searchQuery = addSearchQueryFacet(
    searchQuery,
    'rating_bayesian_10_point',
    value,
    'facet=rating_bayesian_10_point:' + value,
  );

  return searchQuery;
}

export function getBayesianRating(searchQuery: SearchQuery): number {
  let value = 0;

  if (some(getFacetList(searchQuery, 'rating_bayesian_10_point'))) {
    value = getFacetList(searchQuery, 'rating_bayesian_10_point')[0];
  }

  return value;
}

export function dishMatching(searchQuery: SearchQuery): string | boolean {
  return searchQuery.queryText || some(getFacetList(searchQuery, 'dishes'));
}

export function getFacetList(searchQuery: SearchQuery, type: string): any[] {
  type = initializeFacetCategory(searchQuery, type);

  return searchQuery.extraParameters.facet[type].map(
    (facetType) => facetType.value,
  );
}

export function getFacetsOfType(searchQuery: SearchQuery, type: string): any[] {
  type = initializeFacetCategory(searchQuery, type);

  return searchQuery.extraParameters.facet[type];
}

/**
 * Takes in routeParams as a dictionary and replaces the queryText, orderMethod, facets, and sort
 * if they exist in the routeParam. This is primarily used for the search page
 *
 * @param routeParams - dictionary of query parameters from the diner.
 */
export function importRouteParams(
  searchQuery: SearchQuery,
  routeParams: any,
): SearchQuery {
  routeParams = routeParams || {};

  if (routeParams.queryText) {
    searchQuery = setSearchQueryText(searchQuery, routeParams.queryText);
  }

  if (routeParams.orderMethod) {
    searchQuery.orderMethod = routeParams.orderMethod;
  }

  if (routeParams.facetSet) {
    searchQuery.facetSet = routeParams.facetSet;
  }

  if (routeParams.facetCount) {
    // a single facetCount will return a string, but multiple facetCounts will return an array of strings
    let facetCount = routeParams.facetCount;
    if (!Array.isArray(facetCount)) {
      facetCount = [facetCount];
    }

    facetCount.forEach((facet) => {
      const tokenizer = facet.split(':');
      if (tokenizer.length > 1) {
        const type = tokenizer[0];
        const count = parseInt(tokenizer[1], 10);
        searchQuery = addFacetCount(searchQuery, type, count);
      }
    });
  }

  if (routeParams.facet) {
    let facetsToAdd = [];

    if (routeParams.facet.constructor !== Array) {
      facetsToAdd.push(routeParams.facet);
    } else {
      facetsToAdd = facetsToAdd.concat(routeParams.facet);
    }

    forEach(facetsToAdd, (facet) => {
      const facetTokenizer = facet.split(':');

      if (facetTokenizer.length > 1) {
        const facetType = facetTokenizer[0];
        let facetValue = facetTokenizer[1];
        // Must be careful with this - assumes that the linkFragment will be in format facet=dish%3APizza
        const linkFragment = 'facet=' + encodeURIComponent(facet);
        const facetTypeGroup = getFacetTypeGroup(facetType);

        if (facetTypeGroup === 'most_popular') {
          facetValue = getFacetValue(facetType);
        }

        searchQuery = addSearchQueryFacet(
          searchQuery,
          facetTypeGroup,
          facetValue,
          linkFragment,
        );
      }
    });
  }

  if (routeParams.contentTag) {
    searchQuery = setContentTag(searchQuery, routeParams.contentTag);
  }

  if (routeParams.radius) {
    searchQuery = setDistance(searchQuery, routeParams.radius);
  }

  if (routeParams.whenFor) {
    searchQuery = setWhenFor(searchQuery, routeParams.whenFor);
  }

  if (routeParams.variationId) {
    searchQuery = setVariationId(searchQuery, routeParams.variationId);
  }

  if (routeParams.fieldSet) {
    searchQuery = setFieldSet(searchQuery, routeParams.fieldSet);
  }

  if (routeParams.whenFrom && routeParams.whenTo) {
    searchQuery.whenFrom = routeParams.whenFrom;
    searchQuery.whenTo = routeParams.whenTo;
  }

  if (routeParams.sortSetId) {
    searchQuery = setSortSetId(searchQuery, routeParams.sortSetId);
  }

  searchQuery = importBrowseRouteParams(searchQuery, routeParams);

  return searchQuery;
}

/**
 * Takes in routeParams as a dictionary and replaces sort and pageNum if applicable.  This is primarily
 * used in the browse pages since we do not want to pull in other data like facetSet accidentally
 *
 * @param routeParams
 */
export function importBrowseRouteParams(
  searchQuery: SearchQuery,
  routeParams,
): SearchQuery {
  routeParams = routeParams || {};

  if (routeParams.sorts) {
    const sortValue = routeParams.sorts;

    if (sortValue) {
      const linkFragment = 'sorts=' + encodeURIComponent(sortValue);
      searchQuery = setSearchQuerySort(searchQuery, sortValue, linkFragment);
    }
  }

  if (routeParams.restaurant_group) {
    searchQuery = addSearchQueryFacet(
      searchQuery,
      'restaurant_group',
      'Restaurant Group',
      'facet=restaurant_group%3A' + routeParams.restaurant_group,
    );
  }

  if (routeParams.pageNum) {
    searchQuery = setPageSearchQuery(searchQuery, routeParams.pageNum);
  }

  return searchQuery;
}

/**
 * Creates a copy of the search query model
 *
 * @returns {SearchQuery}
 */
export function copy(searchQuery: SearchQuery): SearchQuery {
  const copySearchQuery = new SearchQuery();
  // TODO - we might need to actually make a copy of the address but this method is primarily for facets
  copySearchQuery.address = searchQuery.address;
  copySearchQuery.queryText = searchQuery.queryText;
  copySearchQuery.extraParameters = cloneDeep(searchQuery.extraParameters);
  copySearchQuery.orderMethod = searchQuery.orderMethod;
  copySearchQuery.facetSet = searchQuery.facetSet;
  copySearchQuery.whenFor = searchQuery.whenFor;
  copySearchQuery.contentTag = searchQuery.contentTag;
  copySearchQuery.pageSize = searchQuery.pageSize;
  copySearchQuery.variationId = searchQuery.variationId;

  return copySearchQuery;
}

/**
 *  Sets up the facets according to the first time searching user
 */
export function setPresetValues(searchQuery: SearchQuery): SearchQuery {
  const orderType: string = isCateringSearch(searchQuery)
    ? AvailableOrderTypes.CATERING
    : AvailableOrderTypes.STANDARD;
  searchQuery.contentTag = undefined;
  forEach(presetFacets[orderType], (singlePresetFacet) => {
    searchQuery = addSearchQueryFacet(
      searchQuery,
      singlePresetFacet[0],
      singlePresetFacet[1],
      singlePresetFacet[2],
    );
  });
  // Makes a memoized, hash map of the preset facets.
  // Used to resolve whether or not facets have been
  // added beyond the precets.
  searchQuery = setPresetFacetHashMap(searchQuery);
  return searchQuery;
}

/**
 *  Increments the next page parameter
 */
/*eslint-disable curly*/
export function nextPage(searchQuery: SearchQuery): SearchQuery {
  if (searchQuery.extraParameters.pageNum) {
    searchQuery.extraParameters.pageNum++;
  } else {
    searchQuery.extraParameters.pageNum = 2;
  }

  return searchQuery;
}

/*eslint-enable curly*/
export function setPageSearchQuery(
  searchQuery: SearchQuery,
  pageNum?: any,
): SearchQuery {
  if (!searchQuery.extraParameters) {
    searchQuery.extraParameters = {};
  }
  searchQuery.extraParameters.pageNum = parseInt(pageNum, 10);
  return searchQuery;
}

export function getPageSearchQuery(searchQuery: SearchQuery) {
  return searchQuery.extraParameters.pageNum;
}

// neccessary hack to preserve the setSort legacy method
export function setSortProperty(
  searchQuery: SearchQuery,
  sort: any,
): SearchQuery {
  searchQuery.sort = sort;

  return searchQuery;
}

export function setSearchQuerySort(
  searchQuery: SearchQuery,
  sortKey?: string,
  linkFragment?: string,
): SearchQuery {
  if (sortKey) {
    const sort = {
      key: sortKey,
      linkFragment,
    };

    searchQuery.sort = sort;
  } else {
    delete searchQuery.sort;
  }

  searchQuery = setPageSearchQuery(searchQuery);

  return searchQuery;
}

export function getSearchQuerySort(searchQuery: SearchQuery): Sort {
  return searchQuery.sort;
}

export function getSearchQueryPageSize(searchQuery: SearchQuery): number {
  return searchQuery.pageSize || DEFAULT_PAGE_SIZE;
}

export function setSearchQueryPageSize(
  searchQuery: SearchQuery,
  size: any,
): SearchQuery {
  const parsedValue = parseInt(size, 10);

  if (isNaN(parsedValue)) {
    searchQuery.pageSize = DEFAULT_PAGE_SIZE;
  } else {
    searchQuery.pageSize = parsedValue;
  }

  return searchQuery;
}

export function setSearchMetrics(
  searchQuery: SearchQuery,
  value: boolean,
): SearchQuery {
  searchQuery.searchMetrics = value;
  return searchQuery;
}

export function setCountOmittingTimes(
  searchQuery: SearchQuery,
  value: boolean,
): SearchQuery {
  searchQuery.countOmittingTimes = value;
  return searchQuery;
}

export function setFacetSet(
  searchQuery: SearchQuery,
  facetSet: any,
): SearchQuery {
  if (facetSet) {
    searchQuery.facetSet = facetSet;
  }

  return searchQuery;
}

export function getFacetSet(searchQuery: SearchQuery): any {
  return searchQuery.facetSet;
}

export function getVariationId(searchQuery: SearchQuery): string {
  return searchQuery.variationId;
}

export function setVariationId(
  searchQuery: SearchQuery,
  variationId?: string,
): SearchQuery {
  searchQuery.variationId = variationId;

  return searchQuery;
}

export function setFieldSet(
  searchQuery: SearchQuery,
  fieldSet: any,
): SearchQuery {
  searchQuery.fieldSet = fieldSet;

  return searchQuery;
}

export function isNonPresetFacetApplied(searchQuery: SearchQuery): boolean {
  const facetsApplied: string[] = Object.keys(getFacetHashMap(searchQuery));
  const orderType: string = isCateringSearch(searchQuery)
    ? AvailableOrderTypes.CATERING
    : AvailableOrderTypes.STANDARD;
  let isNonPresetApplied = false;

  if (facetsApplied.length === 0) {
    return true;
  }

  for (let i = 0; i < facetsApplied.length && !isNonPresetApplied; i++) {
    for (
      let j = 0;
      j < presetFacets[orderType].length && !isNonPresetApplied;
      j++
    ) {
      if (facetsApplied[i].indexOf(presetFacets[orderType][j][2]) === -1) {
        isNonPresetApplied = true;
      }
    }
  }

  return isNonPresetApplied;
}

export function setUserHasSearched(
  searchQuery: SearchQuery,
  flag: boolean,
): SearchQuery {
  searchQuery.userHasSearched = flag;

  return searchQuery;
}

export function getUserHasSearched(searchQuery: SearchQuery): boolean {
  return searchQuery.userHasSearched;
}

export function setSortSetId(
  searchQuery: SearchQuery,
  sortSetId: string,
): SearchQuery {
  searchQuery.sortSetId = sortSetId;

  return searchQuery;
}

export function setWhenFor(
  searchQuery: SearchQuery,
  whenFor: string,
): SearchQuery {
  if (whenFor === 'ASAP' || !whenFor) {
    searchQuery.whenFor = undefined;
  } else {
    searchQuery.whenFor = whenFor;
  }

  return searchQuery;
}

export function getWhenFor(searchQuery: SearchQuery): string {
  return searchQuery.whenFor;
}

// export function getDateTime(searchQuery: SearchQuery): DateTime {
//     if (searchQuery.whenFor) {
//         return new DateTime(searchQuery.whenFor, getTimezoneId(searchQuery));
//     } else {
//         return new DateTime(undefined, getTimezoneId(searchQuery));
//     }
// }

export function setContentTag(
  searchQuery: SearchQuery,
  contentTag: string,
): SearchQuery {
  searchQuery.contentTag = contentTag;

  return searchQuery;
}

export function getContentTag(searchQuery: SearchQuery): string {
  return searchQuery.contentTag;
}

export function setSearchId(
  searchQuery: SearchQuery,
  searchId?: string,
): SearchQuery {
  searchQuery.searchId = searchId;

  return searchQuery;
}

export function getTimezoneId(searchQuery: SearchQuery): string {
  return searchQuery.address && searchQuery.address.timezoneId
    ? searchQuery.address.timezoneId
    : 'DEFAULT';
}

export function getOrderMethod(searchQuery: SearchQuery): OrderMethod {
  return searchQuery.orderMethod;
}

export function isPickup(searchQuery: SearchQuery): boolean {
  return searchQuery.orderMethod === OrderMethod.PICKUP;
}

export function isDelivery(searchQuery: SearchQuery): boolean {
  return searchQuery.orderMethod === OrderMethod.DELIVERY;
}

export function isCatering(searchQuery: SearchQuery): boolean {
  return searchQuery.orderMethod === OrderMethod.CATERING;
}

export function resetDynamicFacets(
  searchQuery: SearchQuery,
  facetSettings: FacetSettings = {},
): SearchQuery {
  searchQuery.extraParameters.facet = searchQuery.extraParameters.facet || {};
  delete searchQuery.extraParameters.facet.cuisine;
  delete searchQuery.extraParameters.facet.browse_hierarchy;
  delete searchQuery.extraParameters.facet.non_dietary_cuisine;
  delete searchQuery.extraParameters.facet.dish;
  delete searchQuery.extraParameters.facet.dishes;
  delete searchQuery.extraParameters.facet.dietary_cuisine;
  delete searchQuery.extraParameters.pageNum;
  delete searchQuery.extraParameters.delivery_time;
  delete searchQuery.extraParameters.facet.brand_name;
  delete searchQuery.extraParameters.facet.brand_id_uncollapsed;

  if (!facetSettings.keepOrderType) {
    delete searchQuery.extraParameters.facet.order_type;
  }

  return searchQuery;
}

export function setAddress(
  searchQuery: SearchQuery,
  address?: Address,
): SearchQuery {
  searchQuery.address = address;

  return searchQuery;
}

export function getAddress(searchQuery: SearchQuery): Address {
  return searchQuery.address;
}

export function removeSort(searchQuery: SearchQuery): SearchQuery {
  delete searchQuery.sort;

  return searchQuery;
}

export function setExtraParameters(
  searchQuery: SearchQuery,
  extraParamenters: SearchQuery,
): SearchQuery {
  searchQuery.extraParameters = extraParamenters as any;
  return searchQuery;
}

export function removeOrderMethodSpecificFacets(
  searchQuery: SearchQuery,
): SearchQuery {
  // leaving this temporarily to remove pre-exisitng rapid pickup facets from users
  searchQuery = removeSearchQueryFacet(
    searchQuery,
    'most_popular',
    'facet=rapid_pickup%3Atrue',
  );

  if (searchQuery.orderMethod === OrderMethod.PICKUP) {
    searchQuery = removeSearchQueryFacet(
      searchQuery,
      'most_popular',
      'facet=free_delivery%3Atrue',
    );
    searchQuery = removeSearchQueryFacet(
      searchQuery,
      'most_popular',
      'facet=order_tracking_enabled%3Atrue',
    );
    searchQuery = clearSearchQueryFacet(searchQuery, 'delivery_time');
  }

  return searchQuery;
}

export function isCateringSearch(searchQuery: SearchQuery): boolean {
  return (
    !!searchQuery &&
    !!getFacetHashMap(searchQuery)[
      `order_type:facet=order_type%3A${AvailableOrderTypes.CATERING}`
    ]
  );
}

export function isOnlyOpenNow(searchQuery: SearchQuery): boolean {
  const hashMap = getFacetHashMap(searchQuery);

  return (
    Object.keys(hashMap).length === 1 &&
    !searchQuery.queryText &&
    isOpenNowFacet(searchQuery)
  );
}

/**
 * Yes, this could be more generic and reusable, but really we don't care much
 * about the other facets.
 */
export function isOpenNowFacet(searchQuery: SearchQuery): boolean {
  return !!getFacetHashMap(searchQuery)['most_popular:facet=open_now%3Atrue'];
}

export function setSponsoredSize(
  searchQuery: SearchQuery,
  size: number,
): SearchQuery {
  searchQuery.sponsoredSize = size || 0;

  return searchQuery;
}

export function setLocationMode(
  searchQuery: SearchQuery,
  locationMode: OrderMethod,
) {
  searchQuery.locationMode = locationMode;

  return searchQuery;
}

export function getSponsoredSize(searchQuery: SearchQuery): number {
  return searchQuery.sponsoredSize;
}

export function getBreadcrumbsForFacet(
  searchQuery: SearchQuery,
  firstSplitOnChar: string,
  secondSplitOnChar: string,
  returnIndex: number,
  facetName: string,
): SearchQueryBreadCrumb[] {
  let hierarchy = [];
  let breadcrumbs = [];
  const facetHashMapData = getFacetHashMap(searchQuery);

  function hierarchyReducer(accumulator, item, index, collection) {
    const fragment = collection.slice(0, index + 1).join('%2F');
    accumulator.push({
      value: item.replace(/\+/g, ' '),
      link_fragment: `facet=${facetName}%3A${fragment}`,
    });
    return accumulator;
  }
  for (const linkFragment in facetHashMapData) {
    // example linkFragment key in the hash map:
    // browse_hierarchy:facet=browse_hierarchy:sushi/shrimp/shrimp+tempura+roll
    if (new RegExp(facetName).test(linkFragment)) {
      const firstSplit =
        decodeURIComponent(linkFragment).split(firstSplitOnChar);
      hierarchy = firstSplit[returnIndex].split(secondSplitOnChar);

      breadcrumbs = hierarchy.reduce(hierarchyReducer, []);
    }
  }

  return breadcrumbs;
}

/**
 *
 * returns true when only the preset facets are attached.
 * otherise returns false (i.e. when additional facets
 * have been applied.)
 *
 * The goal is to make this test for whether
 * query is in an initial state or not.
 *
 * @returns boolean
 */
export function hasOnlyPresetFacets(searchQuery: SearchQuery): boolean {
  const hashMap = getFacetHashMap(searchQuery) || {};
  const presetHashMap = presetFacetHashMap(searchQuery);
  const misMatches = Object.keys(hashMap).filter((key) => {
    const presetVal = presetHashMap[key];
    const hashMapVal = hashMap[key];
    if (presetVal === undefined || hashMapVal !== presetVal) {
      return true;
    }
    return false;
  });
  return misMatches.length === 0;
}
/**
 *
 * Determines if any facets have been applied to
 * the search query by checking if the hash map
 * is empty.
 *
 * @returns boolean
 *
 */
export function hasFacets(searchQuery: SearchQuery): boolean {
  const res = !isEmpty(getFacetHashMap(searchQuery));
  return res;
}

function setPresetFacetHashMap(searchQuery: SearchQuery): SearchQuery {
  const presetHashMapMemo = {};
  const orderType: string = isCateringSearch(searchQuery)
    ? AvailableOrderTypes.CATERING
    : AvailableOrderTypes.STANDARD;
  presetFacets[orderType].forEach((ft: FacetTuple) => {
    const [type, , linkFragment] = ft;
    presetHashMapMemo[hashFacetObject({ type, linkFragment })] = true;
  });
  searchQuery.presetFacetHashMapMemo = presetHashMapMemo;
  return searchQuery;
}
